import React, { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Spin } from "antd";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import {
  RequestDetailBoxLayoutProps,
  RequestDetailBoxProposalCardProps,
} from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestDetailBox/RequestDetailBoxLayout/requestDetailBoxLayout.model";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { RequestProposalCard } from "@companion-core/shared/app/Components/Requests/RequestProposalCard";
import { RequestDetailContent } from "@companion-core/shared/app/Components/Requests/RequestDetail/requestDetail.model";
import { APIRecommendation } from "@companion-core/shared/app/Interfaces/API";
import { getFamilyIcon } from "@companion-core/shared/app/Utils/myRequests";
import { CrossSellSelection } from "@companion-core/shared/app/Components/CrossSell/CrossSellSelection/CrossSellSelection";
import {
  getProposalCardAriaLabel,
  getProposalCardHref,
} from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestDetailBox/RequestDetailBoxLayout/requestDetailBoxProposalCard.utils";

import "@companion-core/web/src/Assets/Styles/scss/myRequestsDetail.scss";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";

const RequestDetailBoxProposalCard = ({
  requestId,
  status,
  hasDigitalProposal,
  proposalsData,
  family,
  subFamily,
  performDisplayProposals,
  performDisplayRecommendationDetail,
}: RequestDetailBoxProposalCardProps) => {
  const { t } = useTranslation();
  const recommendationValidated = useMemo(() => {
    return proposalsData?.data?.results
      ?.find((value) => value.status === "validated")
      ?.recommendations?.find(
        (recommendation: APIRecommendation) => recommendation.status == "validated"
      );
  }, [proposalsData]);
  const proposalCardHref = useMemo(
    () => getProposalCardHref(status, requestId, proposalsData),
    [status, requestId, proposalsData]
  );
  const proposalCardAriaLabel = useMemo(() => getProposalCardAriaLabel(status, t), [status, t]);

  return (
    <>
      {["recommended", "proposal-rejected"].includes(status) && hasDigitalProposal && (
        <RequestProposalCard
          testID={`request-detail__box-proposal-card--${status}`}
          icon="JP-Bell"
          href={proposalCardHref}
          ariaLabel={proposalCardAriaLabel}
          onClick={performDisplayProposals}
          subtitle={t(`request.my-requests.detail.proposal-card.${status}.subtitle`)}
        />
      )}
      {(status === "proposal-validated" || (status === "booked" && recommendationValidated)) &&
        hasDigitalProposal && (
          <RequestProposalCard
            testID={`request-detail__box-proposal-card--${status}`}
            media={
              (recommendationValidated?.images as Array<{ url: string }> | undefined)?.[0]?.url
            }
            mediaAlt={
              (
                recommendationValidated?.images as Array<{ description_alt?: string }> | undefined
              )?.[0]?.description_alt
            }
            iconOverlay={
              (!recommendationValidated?.images ||
              (recommendationValidated?.images as Array<Record<string, unknown>>)?.length === 0
                ? getFamilyIcon(family, subFamily)
                : undefined) || undefined
            }
            href={proposalCardHref}
            ariaLabel={proposalCardAriaLabel}
            onClick={() => {
              const validatedProposal = proposalsData?.data?.results?.find(
                (value) => value.status === "validated"
              );
              performDisplayRecommendationDetail(
                validatedProposal?.id ?? "",
                recommendationValidated?.id ?? ""
              );
            }}
            title={t(`request.my-requests.detail.proposal-card.${status}.title`)}
            subtitle={recommendationValidated?.title}
          />
        )}
    </>
  );
};

export const RequestDetailBoxLayout = ({
  title,
  goBack,
  backHref,
  backAriaLabel,
  content,
  requestId,
  family,
  subFamily,
  status,
  hasDigitalProposal,
  proposalsData,
  isProposalsPending,
  performDisplayProposals,
  performDisplayRecommendationDetail,
  children,
}: RequestDetailBoxLayoutProps) => {
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const { config } = useConfigStore();
  const isCrossSellEnabled = useMemo(() => config?.modules?.crossSell?.enabled, [config]);
  const backLinkRef = useRef<HTMLAnchorElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const isSubView = content !== undefined && content !== RequestDetailContent.detail;
  useEffect(() => {
    if (!isSubView) return;

    document.title = title;

    const focusTimeout = window.setTimeout(() => {
      (backLinkRef.current ?? backButtonRef.current)?.focus({ preventScroll: true });
    }, 0);

    return () => window.clearTimeout(focusTimeout);
  }, [content, title, isSubView]);

  return isProposalsPending && hasDigitalProposal ? (
    <Spin className="request-detail__loader" spinning={true} />
  ) : (
    <div className="request-detail__box" data-testid="request-detail__box">
      {isMobileSize && content === RequestDetailContent.detail && (
        <RequestDetailBoxProposalCard
          requestId={requestId}
          status={status ?? ""}
          family={family}
          subFamily={subFamily}
          hasDigitalProposal={hasDigitalProposal}
          proposalsData={proposalsData}
          performDisplayProposals={performDisplayProposals}
          performDisplayRecommendationDetail={performDisplayRecommendationDetail}
        />
      )}
      {!isMobileSize && backHref ? (
        <Link
          to={backHref}
          ref={backLinkRef}
          aria-label={backAriaLabel}
          className="request-detail__box-title-container request-detail__box-title-container--with-link"
          data-testid="request-detail__box-back-link">
          <FontIcon
            className="request-detail__box-back-button"
            name="JP-Arrow-left-bold"
            size={18}
            aria-hidden="true"
          />
          <span aria-hidden="true" className="request-detail__box-title title-7">
            {title}
          </span>
        </Link>
      ) : (
        <button
          type="button"
          ref={backButtonRef}
          aria-label={backAriaLabel}
          className="request-detail__box-title-container"
          data-testid="request-detail__box-title-container"
          onClick={() => !isMobileSize && goBack?.()}>
          {!isMobileSize && (
            <FontIcon
              className="request-detail__box-back-button"
              name="JP-Arrow-left-bold"
              size={18}
              aria-hidden="true"
            />
          )}
          <span aria-hidden="true" className="request-detail__box-title title-7">
            {title}
          </span>
        </button>
      )}
      {content === RequestDetailContent.detail && (
        <>
          {!isMobileSize && (
            <RequestDetailBoxProposalCard
              requestId={requestId}
              status={status ?? ""}
              family={family}
              subFamily={subFamily}
              hasDigitalProposal={hasDigitalProposal}
              proposalsData={proposalsData}
              performDisplayProposals={performDisplayProposals}
              performDisplayRecommendationDetail={performDisplayRecommendationDetail}
            />
          )}
          {(status === "booked" || status === "proposal-validated") && isCrossSellEnabled && (
            <CrossSellSelection
              requestId={requestId}
              family={family}
              subFamily={subFamily}
              className="request-detail__box-cross-sells"
            />
          )}
        </>
      )}
      <div className="request-detail__box-content" data-testid="request-detail__box-content">
        {children}
      </div>
    </div>
  );
};
